import { FC, ReactNode, useCallback } from 'react';

import { useFormContext } from 'react-hook-form';
import { useRecoilValue } from 'recoil';

import threeShapeImage from 'assets/images/3shape.png';
import { Modal } from 'components';
import Button from 'components/Button/Button';
import { If } from 'components/Generics';
import { RHFTextArea } from 'components/RHFTextArea/RHFTextArea';
import SpecialNoteTranslation from 'components/SpecialNoteTranslation/SpecialNoteTranslation';
import ThreeShapeFiles from 'components/TreatmentPlanningInstructions/TreatmentFiles/ThreeShapeFiles';
import { TreatmentSourceType } from 'components/TreatmentProgressMenu/TreatmentMenuConfig';
import RHFTreatmentFileUploader from 'containers/TreatmentFileUploader/RHFTreatmentFileUploader/RHFTreatmentFileUploader';
import { useTreatmentContext } from 'contexts/treatment.context';
import { hideLiveTranslation } from 'store/atoms/authAtom';
import { FileType, FilesData } from 'types/Files.types';
import { PatientDetails } from 'types/PatientCard.types';
import { ThreeShapeDocumentUploadUrl } from 'types/ThreeShape';
import { TreatmentDocumentType } from 'types/TreatmentPlanning.types';
import { translate } from 'utils';
import useThreeShapeHooks from 'utils/customHooks/threeShape.hooks';

const mapResimThreeShapeDocToFilesData = (
  doc: ThreeShapeDocumentUploadUrl
): FilesData => {
  return {
    id: doc.key,
    fileType: FileType.FILE,
    fileSrc: doc.url,
    key: doc.key,
    fileName: doc.name,
    documentType: TreatmentDocumentType.NEW_SCAN
  };
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  treatmentId: string;
  title?: ReactNode;
  showResimulationInstructions?: boolean;
  resimulationInstructionsFieldName?: string;
  translatedResimulationFieldName?: string;
  onSubmit?: () => void;
  /** When `onSubmit` is set and this is `false`, the modal stays open until the parent sets `isOpen` false (e.g. after opening a follow-up confirmation). */
  closeAfterSubmit?: boolean;
}

const UploadScansModal: FC<Props> = ({
  isOpen,
  onClose,
  treatmentId,
  title,
  showResimulationInstructions = false,
  resimulationInstructionsFieldName = 'resimulationInstructions',
  translatedResimulationFieldName = 'translatedResimulationInstructions',
  onSubmit,
  closeAfterSubmit = true
}) => {
  const hideLiveTranslationBox = useRecoilValue(hideLiveTranslation);
  const { control, watch, setValue, getValues, setError, clearErrors } =
    useFormContext();
  const resimulationInstructions = watch(resimulationInstructionsFieldName);

  const setSpecialNotes = useCallback(
    // SpecialNoteTranslation passes dynamic field names (main + translated).
    (fieldName: string, result?: string) => {
      setValue(fieldName as never, result as never);
    },
    [setValue]
  );

  const scanFiles = (watch('newScans') as FilesData[]) ?? [];
  const { patientDetails, clinicId, hasShownThreeShapeIntro } =
    useTreatmentContext();

  const { authLoading, handleThreeShapeAuth } = useThreeShapeHooks({
    clinicId,
    treatmentId
  });

  const showThreeShapeConnect = !!(patientDetails as PatientDetails)
    ?.showThreeShape;
  const isScanFiles = scanFiles.length > 0;

  const defaultTitle = translate(
    'retainer.prescription.production_input.upload_new_scans'
  );

  const isScanUploadInProgress = scanFiles.some((file) => {
    const uploadProgress = file.uploadProgress;
    if (uploadProgress === undefined || uploadProgress === null) return false;
    if (Array.isArray(uploadProgress)) {
      return uploadProgress.some((chunkProgress) => chunkProgress < 100);
    }
    return (uploadProgress as number) < 100;
  });

  const handleResimFilesFromThreeShape = useCallback(
    (documents: ThreeShapeDocumentUploadUrl[]) => {
      const prev = (getValues('newScans') as FilesData[]) ?? [];
      const appended = documents.map(mapResimThreeShapeDocToFilesData);
      setValue('newScans' as never, [...prev, ...appended] as never);
    },
    [getValues, setValue]
  );

  const handleSave = () => {
    if (onSubmit) {
      if (scanFiles.length < 2) {
        setError('newScans', {
          message: translate('min.stl.files.required', { X: 2 })
        });
        return;
      }
      clearErrors('newScans');
      onSubmit();
    }

    if (!onSubmit || closeAfterSubmit) {
      onClose();
    }
  };

  const handleClose = () => {
    if (isScanUploadInProgress) {
      return;
    }
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      showCloseIcon={false}
      classNames={{
        modal: 'p-0 md:max-w-[1160px] w-5/6'
      }}
    >
      <section className='w-full'>
        <div className='flex items-center justify-between px-6 py-5 '>
          <h2 className='text-xl font-semibold text-DEFAULT_TEXT'>
            {title ?? defaultTitle}
          </h2>
          <div className='flex items-center gap-4'>
            <Button
              type='button'
              onClick={handleClose}
              className='px-6 py-3'
              disabled={isScanUploadInProgress}
            >
              {translate('cancel.cancel')}
            </Button>
            <Button
              type='button'
              onClick={handleSave}
              className='px-6 py-3 bg-PRIMARY rounded-4xl text-WHITE'
              disabled={
                isScanUploadInProgress ||
                !(
                  scanFiles.length &&
                  (resimulationInstructions?.trim() ||
                    !showResimulationInstructions)
                )
              }
            >
              {onSubmit
                ? translate('submit_button_rounded_(short).button')
                : translate('button_rounded_(short).save')}
            </Button>
          </div>
        </div>
        <div className='px-6 py-8 overflow-y-auto max-h-[500px]'>
          <If condition={showResimulationInstructions}>
            <div className='mb-6 flex flex-col gap-6 md:flex-row'>
              <div className='text-base font-semibold text-DEFAULT_TEXT md:w-1/3'>
                {translate('Rejection.Resimulation_instructions_(Optional)')}
                <span className='text-ERROR'>*</span>
              </div>
              <div className='flex-1 md:w-2/3'>
                {hideLiveTranslationBox ? (
                  <RHFTextArea
                    inputProps={{
                      placeholder: translate('tell_us_more.tell_us_more'),
                      disabled: false
                    }}
                    name={resimulationInstructionsFieldName}
                    autogrow
                  />
                ) : (
                  <SpecialNoteTranslation
                    setValue={setSpecialNotes}
                    isDisabled={false}
                    placeholder={translate('tell_us_more.tell_us_more')}
                    InputTextFieldName={resimulationInstructionsFieldName}
                    translatedTextFieldName={translatedResimulationFieldName}
                  />
                )}
              </div>
            </div>
          </If>
          <div className='flex flex-col md:flex-row gap-6'>
            <div className='md:w-1/3'>
              <h3 className='text-base font-semibold text-DEFAULT_TEXT'>
                {translate('scans.scans')}
              </h3>
              <p className='mt-4 text-base text-DEFAULT_TEXT'>
                {translate('drag.drop.photo', {
                  X: translate('scans.scans').toLowerCase()
                })}
              </p>
              {!!showThreeShapeConnect &&
                !patientDetails?.isLoggedInToThreeShape &&
                (hasShownThreeShapeIntro || isScanFiles) && (
                  <Button
                    className='w-[164px] h-7 text-sm border border-DEFAULT_TEXT py-0 mt-4'
                    onClick={handleThreeShapeAuth}
                    disabled={authLoading}
                  >
                    <span className='flex items-center gap-2 pt-1'>
                      {translate('common.connect')}
                      <img
                        src={threeShapeImage}
                        alt='three shape'
                        className='mb-1 w-19'
                      />
                    </span>
                  </Button>
                )}
            </div>
            <div className='flex-1 md:w-2/3'>
              <div className='w-full'>
                <RHFTreatmentFileUploader
                  control={control}
                  treatmentId={treatmentId}
                  name='newScans'
                  fileDropAreaTitle='scans'
                  documentType={TreatmentDocumentType.NEW_SCAN}
                  sourceType={TreatmentSourceType.PRESCRIPTIONS}
                  dropContainerClassName='bg-GRAY5 rounded-xl py-13 md:py-0'
                  className='h-fit'
                  showError={!!onSubmit}
                  allFiles={scanFiles}
                  skipAttachTreatmentDocuments
                />
              </div>
              <If condition={!!patientDetails?.isLoggedInToThreeShape}>
                <ThreeShapeFiles
                  getResimFiles
                  threeShapeTreatmentId={treatmentId}
                  onResimFilesFromThreeShape={handleResimFilesFromThreeShape}
                />
              </If>
            </div>
          </div>
        </div>
      </section>
    </Modal>
  );
};

export default UploadScansModal;
